import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { GroupDailySpendSeriesResponse, MyGroupUsageResponse } from "../../api/organisationReporting";
import { formatMoney, formatWholeMoney } from "../consumption/formatters";
import type { SectionLayoutMode } from "../layout/sectionLayoutContext";
import card from "../tools/ToolCard.module.css";
import { GroupBurnUpChart } from "./GroupBurnUpChart";
import { computeGroupPace } from "./groupPace";
import { AllowanceMeter, DailyToolBars, ToolSplit } from "./GroupParts";
import { kindKeys, roleText, statusText, statusTones, summaryText } from "./groupText";
import { PeopleLimitChips } from "./PeopleLimitChips";
import styles from "./GroupCard.module.css";
import { Tooltip } from "../shared/Tooltip";

/**
 * One group on "My Groups", written for the person who owns it: how the group as a whole stands
 * against its allowance this month, where it is heading, and how its spend divides between tools.
 * The layout sets the depth: the dense grid is a scoreboard (status, meter, key figures and a small
 * trend), the loose grid adds the summary sentence and the full month chart, and the single column
 * adds each day's spend by tool.
 */
export function GroupCard({
  group,
  series,
  period,
  isCurrentMonth,
  mode,
}: {
  group: MyGroupUsageResponse;
  series: GroupDailySpendSeriesResponse | null;
  period: { periodStart: string; periodEnd: string; generatedAt: string };
  isCurrentMonth: boolean;
  mode: SectionLayoutMode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const headingId = `my-groups-${group.groupId}-heading`;
  const currency = group.currency;
  // The allowance is held in US dollars, so it can only be set against a total in dollars.
  const allowance = currency === "USD" ? group.totalAllowance : null;
  const projected = isCurrentMonth ? (series?.projection?.projectedTotal ?? null) : null;
  const pace = computeGroupPace(group.totalSpend ?? 0, allowance, projected, group.personCount, period, isCurrentMonth);
  const dense = mode === "dense";
  const hasSpend = group.totalSpend !== null;

  const kindLabel = t(kindKeys[group.kind]);
  const roleLabel = roleText(t, group);
  const path = group.breadcrumbs.join(" › ");
  // The parent is what tells same-named groups apart, so a longer path shows just the parent and keeps the rest for the tooltip.
  const shortPath = group.breadcrumbs.length > 1 ? `… › ${group.breadcrumbs[group.breadcrumbs.length - 1]}` : path;

  return (
    <section className={`${card.card} ${card.surface} ${styles.groupCard}`} aria-labelledby={headingId} data-spotlight>
      <div className={styles.top}>
        <div className={styles.identity}>
          <p className={styles.eyebrow}>
            <Tooltip label={path || undefined} placement="below" align="start">
              <span className={styles.kind}>{kindLabel}</span>
              {path && <span className={styles.path}>{shortPath}</span>}
            </Tooltip>
          </p>
          <h2 id={headingId} className={`title-mark ${styles.title}`}>
            <Link to={`/my-groups/${encodeURIComponent(group.groupId)}`} className={styles.titleLink}>
              {group.groupLabel}
            </Link>
          </h2>
          <p className={styles.role}>{roleLabel}</p>
        </div>
        {pace.status !== "none" && <span className={`${card.chip} ${statusTones[pace.status]}`}>{statusText(t, pace)}</span>}
      </div>

      {/* Who is near or at a limit is on the group's own page, so the chips take the manager straight to its people. */}
      {(group.peopleAtLimit > 0 || group.peopleNearLimit > 0) && (
        <Link
          to={`/my-groups/${encodeURIComponent(group.groupId)}#group-people`}
          className={styles.limitLink}
          aria-label={t("myGroups.limits.seeWho", { name: group.groupLabel })}
        >
          <PeopleLimitChips reached={group.peopleAtLimit} near={group.peopleNearLimit} />
          <span aria-hidden="true">›</span>
        </Link>
      )}

      <p className={styles.amount}>
        <span>{formatMoney(pace.spend, currency)}</span>
        {pace.allowance !== null && <span className={styles.ofAllowance}>{t("myGroups.card.ofAllowance", { amount: formatWholeMoney(pace.allowance, currency) })}</span>}
      </p>
      {!group.wasFullyConverted && <p className={styles.note}>{t("myGroups.notFullyConverted")}</p>}

      {/* A group with no allowance keeps the meter's space, so the rows below line up with the cards beside it. */}
      {pace.allowance !== null ? <AllowanceMeter pace={pace} /> : <div className={styles.meterReserved} aria-hidden="true" />}

      {!dense && <p className={mode === "loose" ? `${styles.summary} ${styles.summaryReserved}` : styles.summary}>{summaryText(t, pace, currency)}</p>}

      <dl className={styles.stats}>
        <div>
          <dt>{isCurrentMonth ? t("myGroups.card.projected") : t("myGroups.card.allowanceLeft")}</dt>
          <dd className={pace.status === "heading" || pace.status === "over" ? styles.bad : undefined}>
            {isCurrentMonth
              ? (pace.projected !== null ? formatMoney(pace.projected, currency) : "–")
              : (pace.allowance !== null ? formatMoney(pace.allowance - pace.spend, currency) : "–")}
          </dd>
        </div>
        <div>
          <dt>{t("myGroups.card.perPerson")}</dt>
          <dd>{pace.perPerson !== null ? formatMoney(pace.perPerson, currency) : "–"}</dd>
        </div>
        <div>
          <dt>{t("myGroups.card.activePeople")}</dt>
          <dd>{group.personCount}</dd>
        </div>
      </dl>

      {hasSpend ? (
        <>
          <GroupBurnUpChart series={series} allowance={pace.allowance} currency={currency} period={period} compact={dense} />
          <ToolSplit group={group} compact={dense} />
          {mode === "single" && series && series.days.length > 0 && <DailyToolBars group={group} series={series} />}
        </>
      ) : (
        <p className={styles.empty}>{t("myGroups.card.noSpend")}</p>
      )}
    </section>
  );
}
